import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { WorkflowHelpPanelService, HelpContent } from '../../services/workflow-help-panel/workflow-help-panel.service';
import { AuthService } from '../../../auth/auth.service';
import { Subject, takeUntil } from 'rxjs';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

@Component({
  selector: 'app-workflow-help-panel',
  templateUrl: './workflow-help-panel.component.html',
  styleUrls: ['./workflow-help-panel.component.scss']
})
export class WorkflowHelpPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  currentContent: HelpContent | null = null;
  isMinimized = false;
  isDragging = false;
  isResizing = false;
  
  position: Position = { x: 20, y: 100 };
  size: Size = { width: 400, height: 600 };
  private dragStart: Position = { x: 0, y: 0 };
  private dragOrigin: Position = { x: 0, y: 0 };
  private dragMoved = false;
  private resizeStart: { position: Position; size: Size; cursor: Position } = {
    position: { x: 0, y: 0 },
    size: { width: 0, height: 0 },
    cursor: { x: 0, y: 0 }
  };
  private resizeHandle: string = '';
  
  private readonly minSize: Size = { width: 280, height: 200 };
  readonly minimizedSize: Size = { width: 340, height: 44 };
  private getMaxSize(): Size {
    return {
      width: Math.floor(window.innerWidth * 0.75),
      height: Math.floor(window.innerHeight * 0.75)
    };
  }
  private destroy$ = new Subject<void>();

  constructor(
    private workflowHelpPanelService: WorkflowHelpPanelService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.workflowHelpPanelService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen: boolean) => {
        this.isOpen = isOpen;
        if (isOpen) {
          this.setInitialPosition();
        } else {
          // closed from outside (e.g. logout): next open starts expanded
          this.isMinimized = false;
          this.size = { width: 400, height: 600 };
        }
      });

    this.workflowHelpPanelService.currentContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((content: HelpContent | null) => {
        this.currentContent = content;
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setInitialPosition(): void {
    // Position panel on the right side of the screen
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Position on the right side with some margin
    this.position = {
      x: Math.max(20, windowWidth - this.size.width - 20),
      y: Math.max(20, (windowHeight - this.size.height) / 2)
    };
  }

  private repositionAfterResize(): void {
    // Keep panel on the right side after size changes
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    this.position = {
      x: Math.max(20, windowWidth - this.size.width - 20),
      y: Math.max(20, Math.min(this.position.y, windowHeight - this.size.height - 20))
    };
  }

  closeModal(): void {
    this.workflowHelpPanelService.closeHelp();
    this.isMinimized = false;
    // Reset size to original values
    this.size = { width: 400, height: 600 };
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  startDrag(event: MouseEvent): void {
    // Prevent dragging when clicking control buttons
    if ((event.target as HTMLElement).closest('.control-button')) {
      return;
    }

    this.isDragging = true;
    this.dragMoved = false;
    this.dragOrigin = { x: event.clientX, y: event.clientY };
    this.dragStart = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y
    };

    event.preventDefault();
    event.stopPropagation();
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      this.updateResize(event);
      return;
    }
    
    if (!this.isDragging) return;

    event.preventDefault();

    // a press that barely moves is a click, not a drag
    if (Math.hypot(event.clientX - this.dragOrigin.x, event.clientY - this.dragOrigin.y) > 4) {
      this.dragMoved = true;
    }

    const newX = event.clientX - this.dragStart.x;
    const newY = event.clientY - this.dragStart.y;

    // Keep panel within viewport bounds
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const panelWidth = this.isMinimized ? this.minimizedSize.width : this.size.width;
    const panelHeight = this.isMinimized ? this.minimizedSize.height : this.size.height;

    // Update position directly for immediate response
    this.position = {
      x: Math.max(0, Math.min(newX, windowWidth - panelWidth)),
      y: Math.max(0, Math.min(newY, windowHeight - panelHeight))
    };
  }

  startResize(event: MouseEvent, handle: string): void {
    event.stopPropagation();
    event.preventDefault();

    this.isResizing = true;
    this.resizeHandle = handle;
    this.resizeStart = {
      position: { ...this.position },
      size: { ...this.size },
      cursor: { x: event.clientX, y: event.clientY }
    };
  }

  private updateResize(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();

    const deltaX = event.clientX - this.resizeStart.cursor.x;
    const deltaY = event.clientY - this.resizeStart.cursor.y;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let newWidth = this.resizeStart.size.width;
    let newHeight = this.resizeStart.size.height;
    let newX = this.resizeStart.position.x;
    let newY = this.resizeStart.position.y;

    switch (this.resizeHandle) {
      case 'se': // Southeast corner
        newWidth = this.resizeStart.size.width + deltaX;
        newHeight = this.resizeStart.size.height + deltaY;
        break;
      case 'sw': // Southwest corner
        newWidth = this.resizeStart.size.width - deltaX;
        newHeight = this.resizeStart.size.height + deltaY;
        newX = this.resizeStart.position.x + deltaX;
        break;
      case 'ne': // Northeast corner
        newWidth = this.resizeStart.size.width + deltaX;
        newHeight = this.resizeStart.size.height - deltaY;
        newY = this.resizeStart.position.y + deltaY;
        break;
      case 'nw': // Northwest corner
        newWidth = this.resizeStart.size.width - deltaX;
        newHeight = this.resizeStart.size.height - deltaY;
        newX = this.resizeStart.position.x + deltaX;
        newY = this.resizeStart.position.y + deltaY;
        break;
      case 'n': // North edge
        newHeight = this.resizeStart.size.height - deltaY;
        newY = this.resizeStart.position.y + deltaY;
        break;
      case 's': // South edge
        newHeight = this.resizeStart.size.height + deltaY;
        break;
      case 'e': // East edge
        newWidth = this.resizeStart.size.width + deltaX;
        break;
      case 'w': // West edge
        newWidth = this.resizeStart.size.width - deltaX;
        newX = this.resizeStart.position.x + deltaX;
        break;
    }

    // Apply size constraints
    const maxSize = this.getMaxSize();
    newWidth = Math.max(this.minSize.width, Math.min(maxSize.width, newWidth));
    newHeight = Math.max(this.minSize.height, Math.min(maxSize.height, newHeight));

    // Adjust position if size was constrained for left/top edges
    if (this.resizeHandle.includes('w') && newWidth === this.minSize.width) {
      newX = this.resizeStart.position.x + (this.resizeStart.size.width - this.minSize.width);
    }
    if (this.resizeHandle.includes('n') && newHeight === this.minSize.height) {
      newY = this.resizeStart.position.y + (this.resizeStart.size.height - this.minSize.height);
    }

    // Keep panel within viewport
    if (newX < 0) {
      newWidth += newX;
      newX = 0;
    }
    if (newY < 0) {
      newHeight += newY;
      newY = 0;
    }
    if (newX + newWidth > windowWidth) {
      newWidth = windowWidth - newX;
    }
    if (newY + newHeight > windowHeight) {
      newHeight = windowHeight - newY;
    }

    this.size = { width: newWidth, height: newHeight };
    this.position = { x: newX, y: newY };
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isDragging && this.isMinimized && !this.dragMoved) {
      this.isMinimized = false;
    }
    this.isDragging = false;
    this.isResizing = false;
    this.resizeHandle = '';
    document.body.style.userSelect = '';
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Ensure panel stays within new viewport bounds and adjust size if needed
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const panelWidth = this.isMinimized ? this.minimizedSize.width : this.size.width;
    const panelHeight = this.isMinimized ? this.minimizedSize.height : this.size.height;
    
    // Constrain size to new viewport (75% max)
    if (!this.isMinimized) {
      const maxSize = this.getMaxSize();
      this.size.width = Math.min(this.size.width, maxSize.width);
      this.size.height = Math.min(this.size.height, maxSize.height);
    }

    this.position = {
      x: Math.max(0, Math.min(this.position.x, windowWidth - panelWidth)),
      y: Math.max(0, Math.min(this.position.y, windowHeight - panelHeight))
    };
  }
}
